import { CircularProgress } from '@mui/material';
import { X } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { collapsePills, Pill, PillLinkChip } from 'src/components/Pill';
import { SelectMenu } from 'src/components/SelectMenu';
import {
  ATTRIBUTE_SELECT_MENU_PROPS,
  useAttributeCommit,
  useAttributeMenuOptions,
} from 'src/graphql/entities/custom-attributes/field-view-registry/components/attribute-select-menu';
import { EmptyValue } from 'src/graphql/entities/custom-attributes/field-view-registry/components/empty-value';
import { CustomAttributeEditProps } from 'src/graphql/entities/custom-attributes/field-view-registry/types';
import {
  getAttributeOptionLabel,
  getKindLikeValueSafely,
} from 'src/graphql/entities/custom-attributes/field-view-registry/utils';

const VISIBLE_PILLS_BEFORE_COLLAPSE = 4;
const REMOVE_ICON_SIZE = 11;
const SPINNER_SIZE = '0.875rem';

// Toggling a key off and on again re-appends it, so order is not part of the value.
const sameKeys = (a: readonly string[], b: readonly string[]) =>
  a.length === b.length && a.every((key) => b.includes(key));

export const MultiSelectEditor = ({
  value,
  definition,
  onChange,
  onSave,
  disabled,
  saving,
}: CustomAttributeEditProps) => {
  const saved = getKindLikeValueSafely(value, 'multi_select') ?? [];
  const { options, label } = definition;
  const menuOptions = useAttributeMenuOptions(options);
  const commit = useAttributeCommit({ onChange, onSave });
  const [expanded, setExpanded] = useState(false);

  // While the menu is open the selection lives here and is written once on
  // close. One write per menu session means one real-time echo, so nothing
  // can land between two writes and flip a toggle back.
  const [draft, setDraft] = useState<string[] | null>(null);
  const shown = draft ?? saved;

  const commitKeys = (next: string[]) => {
    if (!sameKeys(next, saved)) {
      commit({ kind: 'multi_select', value: next });
    }
  };

  // Closing the menu is the commit point, but the editor can also unmount with
  // the menu still open (the panel collapses, the route changes). The effect
  // cleanup is the only hook that runs then, and it must see the latest draft,
  // so the draft is mirrored into a ref instead of being captured stale.
  const pendingRef = useRef<{ draft: string[] | null; commitKeys: typeof commitKeys }>({
    draft,
    commitKeys,
  });
  pendingRef.current = { draft, commitKeys };
  useEffect(
    () => () => {
      const pending = pendingRef.current;
      if (pending.draft) pending.commitKeys(pending.draft);
    },
    [],
  );

  const handleOpenChange = (open: boolean) => {
    if (open) {
      setDraft(saved);
      return;
    }
    if (draft) commitKeys(draft);
    setDraft(null);
  };

  const removeKey = (key: string) => {
    const next = shown.filter((candidate) => candidate !== key);
    if (draft) setDraft(next);
    else commitKeys(next);
  };

  const { visible, hiddenCount, isCollapsible } = collapsePills({
    items: shown,
    minVisible: VISIBLE_PILLS_BEFORE_COLLAPSE,
    expanded,
  });

  return (
    <SelectMenu
      {...ATTRIBUTE_SELECT_MENU_PROPS}
      multiple
      label={label}
      options={menuOptions}
      values={shown}
      onValuesChange={setDraft}
      onOpenChange={handleOpenChange}
      disabled={disabled}
      renderTrigger={({ getTriggerProps }) => (
        <>
          {visible.map((key) => {
            const optionLabel = getAttributeOptionLabel(options, key);

            return (
              <Pill
                key={key}
                label={optionLabel}
                hoverAction={
                  disabled
                    ? undefined
                    : {
                        icon: <X size={REMOVE_ICON_SIZE} />,
                        label: `Remove ${optionLabel}`,
                        onClick: () => removeKey(key),
                      }
                }
              />
            );
          })}
          {isCollapsible ? (
            <PillLinkChip type="button" onClick={() => setExpanded((prev) => !prev)}>
              {expanded ? 'Show less' : `+${hiddenCount} more`}
            </PillLinkChip>
          ) : null}
          {disabled ? null : (
            <PillLinkChip {...getTriggerProps()} type="button">
              + Add
            </PillLinkChip>
          )}
          {disabled && shown.length === 0 ? <EmptyValue /> : null}
          {saving ? (
            <CircularProgress size={SPINNER_SIZE} data-testid="multi-select-saving" />
          ) : null}
        </>
      )}
    />
  );
};
